import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Footer from '../../components/feature/Footer';
import ErrorState from '../../components/base/ErrorState';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface CartItem {
  id: number;
  cart_id: number;
  product_id: number;
  quantity_cartons: number;
  product: {
    id: number;
    slug: string;
    title: string;
    brand_id: number | null;
    brand_name?: string;
    main_images: string[];
    carton_size: number;
    carton_stock: number;
  };
  pairs: number;
  unit_price: number | null;
  line_total: number | null;
  tier_message?: string;
}

interface PriceTier {
  id: number;
  product_id: number;
  min_cartons: number;
  max_cartons: number | null;
  unit_price_pair: number;
}

const CartPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartId, setCartId] = useState<number | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    checkAuthAndLoadCart();
  }, []);

  const checkAuthAndLoadCart = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login?redirect=' + encodeURIComponent('/cart'));
        return;
      }

      await loadCart(user.id);
    } catch (error) {
      console.error('Error checking auth:', error);
      setLoading(false);
    }
  };

  const loadCart = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Find active cart
      const { data: cart, error: cartError } = await supabase
        .from('carts')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (cartError && cartError.code !== 'PGRST116') {
        throw cartError;
      }

      if (!cart) {
        setCartItems([]);
        setLoading(false);
        return;
      }

      setCartId(cart.id);

      // Load cart items with product info
      const { data: items, error: itemsError } = await supabase
        .from('cart_items')
        .select('id, cart_id, product_id, quantity_cartons')
        .eq('cart_id', cart.id);

      if (itemsError) throw itemsError;

      const safeItems = Array.isArray(items) ? items : [];

      if (safeItems.length === 0) {
        setCartItems([]);
        setLoading(false);
        return;
      }

      // Load products
      const productIds = safeItems.map(item => item.product_id);
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id, slug, title, brand_id, main_images, carton_size, carton_stock')
        .in('id', productIds);

      if (productsError) throw productsError;

      const safeProducts = Array.isArray(products) ? products : [];

      if (safeProducts.length === 0) {
        setCartItems([]);
        setLoading(false);
        return;
      }

      // Load brands
      const brandIds = safeProducts
        .filter(p => p.brand_id)
        .map(p => p.brand_id);

      let brandsMap: Record<number, string> = {};
      if (brandIds.length > 0) {
        const { data: brands, error: brandsError } = await supabase
          .from('brands')
          .select('id, name')
          .in('id', brandIds);

        if (brandsError) throw brandsError;

        const safeBrands = Array.isArray(brands) ? brands : [];
        brandsMap = safeBrands.reduce((acc, b) => {
          acc[b.id] = b.name;
          return acc;
        }, {} as Record<number, string>);
      }

      // Load price tiers
      const { data: priceTiers, error: tiersError } = await supabase
        .from('price_tiers')
        .select('*')
        .in('product_id', productIds);

      if (tiersError) throw tiersError;

      const safeTiers = Array.isArray(priceTiers) ? priceTiers : [];
      const tiersMap: Record<number, PriceTier[]> = {};
      safeTiers.forEach(tier => {
        if (!tiersMap[tier.product_id]) {
          tiersMap[tier.product_id] = [];
        }
        tiersMap[tier.product_id].push(tier);
      });

      // Build cart items with calculated prices
      const enrichedItems: CartItem[] = safeItems.map(item => {
        const product = safeProducts.find(p => p.id === item.product_id);
        if (!product) {
          return null;
        }

        const cartonSize = product.carton_size || 12;
        const pairs = item.quantity_cartons * cartonSize;

        // Find matching tier
        const tiers = tiersMap[product.id] || [];
        const matchingTier = tiers.find(tier =>
          item.quantity_cartons >= tier.min_cartons &&
          (tier.max_cartons === null || item.quantity_cartons <= tier.max_cartons)
        );

        let unitPrice: number | null = null;
        let lineTotal: number | null = null;
        let tierMessage: string | undefined;

        if (matchingTier) {
          unitPrice = matchingTier.unit_price_pair;
          lineTotal = pairs * unitPrice;
        } else if (tiers.length > 0) {
          tierMessage = 'Tier not defined';
        }

        return {
          id: item.id,
          cart_id: item.cart_id,
          product_id: item.product_id,
          quantity_cartons: item.quantity_cartons,
          product: {
            id: product.id,
            slug: product.slug,
            title: product.title,
            brand_id: product.brand_id,
            brand_name: product.brand_id ? brandsMap[product.brand_id] : undefined,
            main_images: Array.isArray(product.main_images) ? product.main_images : [],
            carton_size: cartonSize,
            carton_stock: product.carton_stock || 0,
          },
          pairs,
          unit_price: unitPrice,
          line_total: lineTotal,
          tier_message: tierMessage,
        };
      }).filter(item => item !== null) as CartItem[];

      setCartItems(enrichedItems);
      setLoading(false);
    } catch (error) {
      console.error('Error loading cart:', error);
      setError('Failed to load cart. Please try again.');
      setCartItems([]);
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId: number, newQuantity: number, maxStock: number) => {
    if (newQuantity < 1) {
      showMessage('Quantity cannot be less than 1. Use Remove button to delete item.');
      return;
    }

    if (newQuantity > maxStock) {
      showMessage(`Maximum available stock is ${maxStock} cartons.`);
      newQuantity = maxStock;
    }

    try {
      setUpdating(itemId);

      const { error } = await supabase
        .from('cart_items')
        .update({ quantity_cartons: newQuantity })
        .eq('id', itemId);

      if (error) throw error;

      // Reload cart to recalculate prices
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await loadCart(user.id);
      }

      setUpdating(null);
    } catch (error) {
      console.error('Error updating quantity:', error);
      showMessage('Failed to update quantity. Please try again.');
      setUpdating(null);
    }
  };

  const removeItem = async (itemId: number) => {
    if (!confirm('Are you sure you want to remove this item from your cart?')) {
      return;
    }

    try {
      setUpdating(itemId);

      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      // Reload cart
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await loadCart(user.id);
      }

      showMessage('Item removed from cart.');
      setUpdating(null);
    } catch (error) {
      console.error('Error removing item:', error);
      showMessage('Failed to remove item. Please try again.');
      setUpdating(null);
    }
  };

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  // Calculate cart summary
  const calculateSummary = () => {
    const totalCartons = cartItems.reduce((sum, item) => sum + item.quantity_cartons, 0);
    const totalPairs = cartItems.reduce((sum, item) => sum + item.pairs, 0);
    const productsSubtotal = cartItems.reduce((sum, item) => {
      return sum + (item.line_total || 0);
    }, 0);

    return {
      totalCartons,
      totalPairs,
      productsSubtotal,
    };
  };

  const handleProceedToCheckout = () => {
    // Validation
    if (!cartId || cartItems.length === 0) {
      showMessage('Your cart is empty. Please add items before proceeding.');
      return;
    }

    const summary = calculateSummary();
    if (summary.totalCartons <= 0) {
      showMessage('Invalid cart quantities. Please check your items.');
      return;
    }

    // Check if all items have valid quantities
    const hasInvalidQuantity = cartItems.some(item => item.quantity_cartons < 1);
    if (hasInvalidQuantity) {
      showMessage('All items must have a quantity of at least 1.');
      return;
    }

    // Navigate to checkout
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading cart...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <ErrorState onRetry={() => {
          const { data: { user } } = supabase.auth.getUser();
          user.then(({ data }) => {
            if (data?.user) {
              loadCart(data.user.id);
            }
          });
        }} />
        <Footer />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add products from the catalog to start an order.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to products
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const summary = calculateSummary();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

        {/* Message */}
        {message && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Product Image */}
                  <div
                    onClick={() => navigate(`/product/${item.product.slug}`)}
                    className="flex-shrink-0 w-full md:w-32 h-32 bg-gray-100 rounded-lg overflow-hidden cursor-pointer"
                  >
                    {item.product.main_images.length > 0 ? (
                      <img
                        src={item.product.main_images[0]}
                        alt={item.product.title}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-4xl text-gray-300"></i>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-grow">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                      <div className="flex-grow">
                        {/* Brand */}
                        {item.product.brand_name && (
                          <p className="text-sm text-gray-500 mb-1">{item.product.brand_name}</p>
                        )}

                        {/* Title */}
                        <h3
                          onClick={() => navigate(`/product/${item.product.slug}`)}
                          className="text-lg font-bold text-gray-900 mb-2 hover:text-blue-600 cursor-pointer"
                        >
                          {item.product.title}
                        </h3>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-4 mb-3">
                          <span className="text-sm text-gray-600">Quantity (cartons):</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity_cartons - 1, item.product.carton_stock)}
                              disabled={updating === item.id || item.quantity_cartons <= 1}
                              className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <input
                              type="number"
                              min="1"
                              max={item.product.carton_stock}
                              value={item.quantity_cartons}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                updateQuantity(item.id, val, item.product.carton_stock);
                              }}
                              disabled={updating === item.id}
                              className="w-20 text-center border border-gray-300 rounded px-2 py-1 disabled:bg-gray-50"
                            />
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity_cartons + 1, item.product.carton_stock)}
                              disabled={updating === item.id || item.quantity_cartons >= item.product.carton_stock}
                              className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                          {updating === item.id && (
                            <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                          )}
                        </div>

                        {/* Pairs Info */}
                        <p className="text-sm text-gray-600 mb-2">
                          {item.quantity_cartons} cartons ({item.pairs} pairs)
                        </p>

                        {/* Price Info */}
                        {item.unit_price !== null ? (
                          <div className="space-y-1">
                            <p className="text-sm text-gray-600">
                              Unit price: <span className="font-semibold text-gray-900">${item.unit_price.toFixed(2)} / pair</span>
                            </p>
                            <p className="text-lg font-bold text-gray-900">
                              Line total: ${item.line_total?.toFixed(2)}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">{item.tier_message || 'Price not available'}</p>
                        )}
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeItem(item.id)}
                        disabled={updating === item.id}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Summary - Right Column */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Cart Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total cartons:</span>
                  <span className="font-semibold text-gray-900">{summary.totalCartons}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total pairs:</span>
                  <span className="font-semibold text-gray-900">{summary.totalPairs}</span>
                </div>
                
                <div className="border-t border-gray-300 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Products subtotal:</span>
                    <span className="text-2xl font-bold text-gray-900">
                      ${summary.productsSubtotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <i className="ri-information-line mr-1"></i>
                  Shipping cost and payment will be confirmed by our sales team.
                </p>
              </div>

              <button
                onClick={handleProceedToCheckout}
                disabled={cartItems.length === 0 || summary.totalCartons <= 0}
                className="w-full bg-black text-white px-6 py-4 rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold text-lg whitespace-nowrap cursor-pointer"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
